import React, { FC, memo, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'

import { objectXCellRenderer } from '@components/TimeLineGrid/helpers'

import { useAppSelector } from '@hooks'
import { TIME_LINE_CELL_MAX_ITEMS_COUNT } from '@constants'
import { TimelineCellItemDragState, TimeLineDTOType, TimelineGhostCell } from '@types'

type Props = {
  config: TimeLineDTOType
}

export const DragGhostOverlay = memo(({ config }: Props) => {
  const dragState = useAppSelector(state => state.timeLineGrid.cellItemDragState)

  if (!dragState) {
    return null
  }

  return <DragGhostOverlayContent config={config} dragState={dragState} />
})

DragGhostOverlay.displayName = 'DragGhostOverlay'

type ContentProps = {
  config: TimeLineDTOType
  dragState: TimelineCellItemDragState
}

const DragGhostOverlayContent: FC<ContentProps> = ({ dragState, config }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (wrapperRef.current) {
      const x = dragState.initialMouseX - dragState.anchorOffsetX
      const y = dragState.initialMouseY - dragState.anchorOffsetY
      wrapperRef.current.style.transform = `translate(${x}px, ${y}px)`
    }
  }, [
    dragState.anchorOffsetX,
    dragState.anchorOffsetY,
    dragState.initialMouseX,
    dragState.initialMouseY,
  ])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (wrapperRef.current) {
        const x = e.clientX - dragState.anchorOffsetX
        const y = e.clientY - dragState.anchorOffsetY
        wrapperRef.current.style.transform = `translate(${x}px, ${y}px)`
      }
    }

    document.addEventListener('mousemove', handleMouseMove)

    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [dragState.anchorOffsetX, dragState.anchorOffsetY])

  return createPortal(
    <div
      ref={wrapperRef}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        willChange: 'transform',
      }}
    >
      {dragState.ghostCells.map((cell: TimelineGhostCell, idx: number) => {
        const visibleEvents = cell.events.slice(0, TIME_LINE_CELL_MAX_ITEMS_COUNT)
        const eventCount = Math.max(visibleEvents.length, 1)
        const cellHeight = config.objectX.cell?.minHeight ?? cell.height

        const mockParams = {
          id: cell.rowId,
          row: cell.rowData,
          field: cell.columnId,
          value: visibleEvents,
          colDef: { computedWidth: cell.width },
        } as GridRenderCellParams

        return (
          <div
            key={`${cell.rowId}-${cell.columnId}-${idx}`}
            style={{
              position: 'absolute',
              left: cell.relX,
              top: cell.relY,
              width: cell.width,
              height: cell.height,
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
              opacity: 0.92,
              borderRadius: 4,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              overflow: 'hidden',
              background: 'white',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {visibleEvents.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  style={{
                    width: '100%',
                    height: `${cellHeight / eventCount}px`,
                  }}
                >
                  {objectXCellRenderer({
                    config,
                    params: mockParams,
                    cellItem: item,
                    cellItemIndex: itemIndex,
                  })}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>,
    document.body
  )
}

DragGhostOverlayContent.displayName = 'DragGhostOverlayContent'
